import React, { useState } from 'react';
import { Box, Card, CardContent, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TablePagination, Chip, IconButton, Menu, MenuItem, Tooltip, FormControl, Select, SelectChangeEvent, InputLabel, OutlinedInput, Checkbox, ListItemText, TextField, Popover, Button, InputAdornment, Divider, useMediaQuery, useTheme, Drawer, Skeleton, LinearProgress } from '@mui/material';
import { Icon } from '@iconify/react';
export interface Column<T> {
  id: string;
  label: string;
  renderCell?: (row: T) => React.ReactNode;
  renderFilter?: () => React.ReactNode;
  filterValue?: any;
  onFilterChange?: (value: any) => void;
  sortable?: boolean;
}
export interface RowAction<T> {
  icon: string;
  label: string;
  onClick: (row: T) => void;
  condition?: (row: T) => boolean;
}
export interface FilterChip {
  id: string;
  label: string;
  value: string | number | boolean;
  onDelete: () => void;
}
export interface DataTableProps<T> {
  title: string;
  data: T[];
  columns: Column<T>[];
  rowActions?: RowAction<T>[];
  keyField: keyof T;
  filterChips?: FilterChip[];
  onClearAllFilters?: () => void;
  loading?: boolean;
  initialVisibleColumns?: string[];
  onSearchChange?: (value: string) => void;
  searchPlaceholder?: string;
  searchValue?: string;
  onExport?: () => void;
  emptyStateMessage?: string;
  rowsPerPageOptions?: number[];
  showColumnFilters?: boolean;
  onRowClick?: (row: T) => void;
  showSearch?: boolean;
}
function DataTable<T>({
  title,
  data,
  columns,
  rowActions,
  keyField,
  filterChips,
  onClearAllFilters,
  loading = false,
  initialVisibleColumns,
  onSearchChange,
  searchPlaceholder = 'Search...',
  searchValue = '',
  onExport,
  emptyStateMessage = 'No data found',
  rowsPerPageOptions = [10, 25, 50, 100],
  showColumnFilters = true,
  onRowClick,
  showSearch = false
}: DataTableProps<T>) {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(rowsPerPageOptions[0]);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedRow, setSelectedRow] = useState<T | null>(null);
  // FIXED: Always include 'actions' in visible columns if rowActions exist
  const [visibleColumns, setVisibleColumns] = useState<string[]>(() => {
    const cols = initialVisibleColumns || columns.map(col => col.id);
    // Add 'actions' if rowActions exist and it's not already in the list
    if (rowActions && !cols.includes('actions')) {
      return [...cols, 'actions'];
    }
    return cols;
  });
  const [columnsMenuAnchor, setColumnsMenuAnchor] = useState<null | HTMLElement>(null);
  const [filterMenuAnchor, setFilterMenuAnchor] = useState<null | HTMLElement>(null);
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [density, setDensity] = useState<'medium' | 'compact'>('medium');
  const [search, setSearch] = useState(searchValue);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, row: T) => {
    setAnchorEl(event.currentTarget);
    setSelectedRow(row);
  };
  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedRow(null);
  };
  const handleColumnsMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setColumnsMenuAnchor(event.currentTarget);
  };
  const handleColumnsMenuClose = () => {
    setColumnsMenuAnchor(null);
  };
  const handleColumnToggle = (columnId: string) => {
    if (visibleColumns.includes(columnId)) {
      if (visibleColumns.length > 1) {
        setVisibleColumns(visibleColumns.filter(col => col !== columnId));
      }
    } else {
      setVisibleColumns([...visibleColumns, columnId]);
    }
  };
  const handleFilterMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    if (isMobile) {
      setFilterDrawerOpen(true);
    } else {
      setFilterMenuAnchor(event.currentTarget);
    }
  };
  const handleFilterMenuClose = () => {
    setFilterMenuAnchor(null);
    setFilterDrawerOpen(false);
  };
  const handleDensityChange = () => {
    setDensity(density === 'medium' ? 'compact' : 'medium');
  };
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearch(value);
    if (onSearchChange) {
      onSearchChange(value);
    }
  };
  const clearSearch = () => {
    setSearch('');
    if (onSearchChange) {
      onSearchChange('');
    }
  };
  const paginatedData = data.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  if (loading) {
    return <Card elevation={0} sx={{
      borderRadius: 2,
      border: '1px solid #E5E7EB'
    }} data-id="element-932" data-mp="1413wnquekspz">
        <CardContent sx={{
        p: 0,
        position: 'relative'
      }} data-id="element-933" data-mp="3amzy6cyu1byg">
          <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          p: 2,
          flexWrap: 'wrap',
          gap: 1
        }} data-id="element-934" data-mp="3flkvo4mp9hj7">
            <Skeleton variant="text" width={150} height={32} data-id="element-935" data-mp="35ugh360xryow" />
            <Box sx={{
            display: 'flex',
            gap: 1
          }} data-id="element-936" data-mp="2rw8sble203ot">
              {[1, 2, 3, 4].map(i => <Skeleton key={i} variant="circular" width={32} height={32} data-id="element-937" data-mp="2hzdqasiknl5s" />)}
            </Box>
          </Box>
          <Divider data-id="element-938" data-mp="2myqdxfh9ow9f" />
          <LinearProgress sx={{
          height: 3,
          background: 'transparent',
          '& .MuiLinearProgress-bar': {
            background: 'linear-gradient(90deg, #22D3EE 0%, #0EA5E9 50%, #0284C7 100%)',
            backgroundSize: '200% 100%',
            animation: 'shimmer 2s infinite'
          },
          '@keyframes shimmer': {
            '0%': {
              backgroundPosition: '200% 0'
            },
            '100%': {
              backgroundPosition: '-200% 0'
            }
          }
        }} data-id="element-939" data-mp="2my76isp8tq9n" />
          <TableContainer data-id="element-940" data-mp="12gfvxq7idc9b">
            <Table size={density} stickyHeader data-id="element-941" data-mp="12ev3q78o25em">
              <TableHead data-id="element-942" data-mp="12hr2tyw9304n">
                <TableRow data-id="element-943" data-mp="xga2c4hohfol">
                  {columns.map(column => visibleColumns.includes(column.id) && <TableCell key={column.id} sx={{
                  backgroundColor: 'white',
                  fontWeight: 600
                }} data-id="element-944" data-mp="so0h31rid3sn">
                          <Skeleton variant="text" width="80%" height={24} data-id="element-945" data-mp="1gew1vexjqyqe" />
                        </TableCell>)}
                </TableRow>
              </TableHead>
              <TableBody data-id="element-946" data-mp="1sqopm7ckdwlb">
                {[...Array(5)].map((_, index) => <TableRow key={index} data-id="element-947" data-mp="1xoqg3dcbky6t" data-mpi={index}>
                    {visibleColumns.map(column => <TableCell key={column} data-id="element-948" data-mp="22ocuhq3vwkh7">
                        <Skeleton variant="text" width="90%" height={24} data-id="element-949" data-mp="22m05iszpl9un" />
                      </TableCell>)}
                  </TableRow>)}
              </TableBody>
            </Table>
          </TableContainer>
          <Divider data-id="element-950" data-mp="2gf7vkg855xa8" />
          <Box p={2} data-id="element-951" data-mp="2ggspq4qjne7k">
            <Skeleton variant="rectangular" width="100%" height={52} data-id="element-952" data-mp="2gg9hjfqp446q" />
          </Box>
        </CardContent>
      </Card>;
  }
  return <Card elevation={0} sx={{
    borderRadius: 2,
    border: '1px solid #E5E7EB',
    maxWidth: '100%',
    overflow: 'hidden'
  }} data-id="element-953" data-mp="2qdxdg901fhqh">
      <CardContent sx={{
      p: 0,
      position: 'relative',
      maxWidth: '100%'
    }} data-id="element-954" data-mp="2vae3j8qss667">
        <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        p: 2,
        flexWrap: 'wrap',
        gap: 1
      }} data-id="element-955" data-mp="3bp5ev7pbzd1v">
          <Typography variant="subtitle1" sx={{
          fontWeight: 500
        }} data-id="element-956" data-mp="3kqlr7kzphp8q">
            {title} ({data.length})
          </Typography>
          <Box sx={{
          display: 'flex',
          gap: 1
        }} data-id="element-957" data-mp="2sz5vn1g68s6d">
            <Tooltip title="Columns" data-id="element-958" data-mp="36qavo4ngbmh8">
              <IconButton size="small" onClick={handleColumnsMenuOpen} data-id="element-959" data-mp="36rdle991fivk">
                <Icon icon="lucide:columns" width={18} height={18} data-id="element-960" data-mp="31xht9nfggq8b" />
              </IconButton>
            </Tooltip>
            {onExport && <Tooltip title="Export" data-id="element-961" data-mp="29af1de5abdr1">
                <IconButton size="small" onClick={onExport} data-id="element-962" data-mp="299diwolxvoza">
                  <Icon icon="lucide:download" width={18} height={18} data-id="element-963" data-mp="orkxjx2r8l7m" />
                </IconButton>
              </Tooltip>}
          </Box>
        </Box>
        {/* Filter chips */}
        {filterChips && filterChips.length > 0 && <Box sx={{
        px: 2,
        pb: 2,
        display: 'flex',
        flexWrap: 'wrap',
        gap: 1
      }} data-id="element-964" data-mp="ft99o2t9w1iv">
            {filterChips.map(chip => <Chip key={chip.id} label={`${chip.label}: ${chip.value}`} onDelete={chip.onDelete} size="small" data-id="element-965" data-mp="1choz7ywduavz" />)}
            {onClearAllFilters && <Chip label="Clear all" onClick={onClearAllFilters} size="small" variant="outlined" data-id="element-966" data-mp="13fxxt68vkq7x" />}
          </Box>}
        <Divider data-id="element-967" data-mp="3o8szssynd3tp" />
        <TableContainer sx={{
        overflowX: 'auto',
        maxWidth: '100%',
        '&::-webkit-scrollbar': {
          height: 8
        },
        '&::-webkit-scrollbar-track': {
          backgroundColor: '#F3F4F6'
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: '#D1D5DB',
          borderRadius: 4,
          '&:hover': {
            backgroundColor: '#9CA3AF'
          }
        }
      }} data-id="element-968" data-mp="3o5535kxwo1ar">
          <Table size="medium" stickyHeader data-id="element-969" data-mp="23jar7h1f3ddg">
            <TableHead data-id="element-970" data-mp="23k29jq607els">
              {showColumnFilters && <TableRow data-id="element-971" data-mp="1pk1amg9auh8y">
                  {columns.map(column => visibleColumns.includes(column.id) && <TableCell key={`filter-${column.id}`} sx={{
                backgroundColor: '#F8FAFC',
                padding: '8px 16px',
                borderBottom: 'none',
                verticalAlign: 'top'
              }} data-id="element-972" data-mp="1ukozava22m00">
                          {column.renderFilter && column.renderFilter()}
                        </TableCell>)}
                  {rowActions && visibleColumns.includes('actions') && <TableCell sx={{
                backgroundColor: '#F8FAFC',
                padding: '8px 16px',
                borderBottom: 'none'
              }} data-id="element-973" data-mp="1i6a74c3g1or3" />}
                </TableRow>}
              <TableRow data-id="element-974" data-mp="25vlam1c6hx8x">
                {columns.map(column => visibleColumns.includes(column.id) && <TableCell key={column.id} sx={{
                backgroundColor: 'white',
                fontWeight: 600,
                transition: 'none'
              }} data-id="element-975" data-mp="bkpqq2vjmvqu">
                        {column.label}
                      </TableCell>)}
                {rowActions && visibleColumns.includes('actions') && <TableCell sx={{
                backgroundColor: 'white',
                fontWeight: 600,
                transition: 'none'
              }} data-id="element-976" data-mp="1a99wme5i6hez">
                    Actions
                  </TableCell>}
              </TableRow>
            </TableHead>
            <TableBody data-id="element-977" data-mp="1o0wzwxxv4q79">
              {paginatedData.map(row => <TableRow key={String(row[keyField])} hover onClick={onRowClick ? () => onRowClick(row) : undefined} sx={{
              cursor: onRowClick ? 'pointer' : 'default',
              '&:hover': onRowClick ? {
                backgroundColor: 'rgba(79, 70, 229, 0.04)'
              } : undefined
            }} data-id="element-978" data-mp="1sxwx0jcqiim8">
                  {columns.map(column => visibleColumns.includes(column.id) && <TableCell key={column.id} data-id="element-979" data-mp="38kabj2wbwqqz">
                          {column.renderCell ? column.renderCell(row) : String(row[column.id as keyof T] || '')}
                        </TableCell>)}
                  {rowActions && visibleColumns.includes('actions') && <TableCell data-id="element-980" data-mp="3rga7spwcaggd">
                      <Box sx={{
                  display: 'flex',
                  justifyContent: 'center'
                }} data-id="element-981" data-mp="3reyzqdvv2mjg">
                        <Tooltip title="Actions" data-id="element-982" data-mp="nyi80pbyos7m">
                          <IconButton size="small" onClick={event => {
                      event.stopPropagation();
                      handleMenuClick(event, row);
                    }} sx={{
                      color: '#64748B',
                      '&:hover': {
                        backgroundColor: 'rgba(100, 116, 139, 0.08)'
                      }
                    }} data-id="element-983" data-mp="8ha4xygldpux">
                            <Icon icon="lucide:more-vertical" width={18} height={18} data-id="element-984" data-mp="hj9mqszfkxyx" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>}
                </TableRow>)}
              {paginatedData.length === 0 && <TableRow data-id="element-985" data-mp="2znhsov6c9fid">
                  <TableCell colSpan={visibleColumns.length + (rowActions && visibleColumns.includes('actions') ? 1 : 0)} align="center" sx={{
                py: 3
              }} data-id="element-986" data-mp="2zmq1rxnos5yf">
                    {emptyStateMessage}
                  </TableCell>
                </TableRow>}
            </TableBody>
          </Table>
        </TableContainer>
        {/* Row Actions Menu */}
        {rowActions && <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose} anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right'
      }} transformOrigin={{
        vertical: 'top',
        horizontal: 'right'
      }} PaperProps={{
        sx: {
          minWidth: 180,
          boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
          borderRadius: 2
        }
      }} data-id="element-987" data-mp="2ps6d6an3w49k">
            {selectedRow && rowActions.filter(action => !action.condition || action.condition(selectedRow)).map((action, index) => <MenuItem key={index} onClick={event => {
          event.stopPropagation();
          action.onClick(selectedRow);
          handleMenuClose();
        }} sx={{
          fontSize: '0.85rem',
          gap: 1.5,
          py: 1,
          color: action.label === 'Cancel' || action.label === 'Cancel Transaction' || action.label === 'Reject' ? '#EF4444' : action.label === 'Approve' ? '#10B981' : '#334155'
        }} data-id="element-988" data-mp="2b4mds63dzohw" data-mpi={index}>
                    <Icon icon={action.icon} width={16} height={16} color={action.label === 'View Details' ? '#0EA5E9' : action.label === 'Amend' ? '#0EA5E9' : action.label === 'Cancel' || action.label === 'Cancel Transaction' || action.label === 'Reject' ? '#EF4444' : action.label === 'Approve' ? '#10B981' : '#64748B'} data-id="element-989" data-mp="1frv0r3wi3sxk" />
                    {action.label}
                  </MenuItem>)}
          </Menu>}
        <TablePagination rowsPerPageOptions={rowsPerPageOptions} component="div" count={data.length} rowsPerPage={rowsPerPage} page={page} onPageChange={handleChangePage} onRowsPerPageChange={handleChangeRowsPerPage} data-id="element-990" data-mp="2b0f5ir6vjdn1" />
      </CardContent>
      {/* Column visibility popover */}
      <Popover open={Boolean(columnsMenuAnchor)} anchorEl={columnsMenuAnchor} onClose={handleColumnsMenuClose} anchorOrigin={{
      vertical: 'bottom',
      horizontal: 'right'
    }} transformOrigin={{
      vertical: 'top',
      horizontal: 'right'
    }} data-id="element-991" data-mp="1s2kmrlfy1nrg">
        <Box sx={{
        p: 2,
        width: 200
      }} data-id="element-992" data-mp="14ekvmkepzvcc">
          <Typography variant="subtitle2" sx={{
          mb: 1
        }} data-id="element-993" data-mp="19bbbb6mm4ox8">
            Show Columns
          </Typography>
          {columns.map(column => <Box key={column.id} sx={{
          display: 'flex',
          alignItems: 'center',
          mb: 0.5
        }} data-id="element-994" data-mp="ksrohwd6zshq">
              <Checkbox checked={visibleColumns.includes(column.id)} onChange={() => handleColumnToggle(column.id)} size="small" disabled={visibleColumns.length === 1 && visibleColumns.includes(column.id)} data-id="element-995" data-mp="uouqn281nr4o" />
              <Typography variant="body2" data-id="element-996" data-mp="1ih2wln3hghtu">{column.label}</Typography>
            </Box>)}
          {rowActions && <Box sx={{
          display: 'flex',
          alignItems: 'center',
          mb: 0.5
        }} data-id="element-997" data-mp="1rfevecei6lxd">
              <Checkbox checked={visibleColumns.includes('actions')} onChange={() => handleColumnToggle('actions')} size="small" disabled={visibleColumns.length === 1 && visibleColumns.includes('actions')} data-id="element-998" data-mp="1nc8i4ksz3uhe" />
              <Typography variant="body2" data-id="element-999" data-mp="23y4zutmaoel9">Actions</Typography>
            </Box>}
        </Box>
      </Popover>
    </Card>;
}
export default DataTable;